import { mergeAttributes, Node, nodePasteRule } from "@tiptap/core"
import { getLoomOEmbedData, isValidLoomUrl, LOOM_REGEX_GLOBAL } from "./loom-utils"

export interface LoomOptions {
    addPasteHandler: boolean
    HTMLAttributes: Record<string, any>
    inline: boolean
    width: number
    height: number
}

type SetLoomVideoOptions = { src: string; width?: number; height?: number }

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        loom: {
            setLoomVideo: (options: SetLoomVideoOptions) => ReturnType
        }
    }
}

export const Loom = Node.create<LoomOptions>({
    name: "loom",

    addOptions() {
        return {
            addPasteHandler: true,
            HTMLAttributes: {},
            inline: false,
            width: 640,
            height: 360,
        }
    },

    inline() {
        return this.options.inline
    },

    group() {
        return this.options.inline ? "inline" : "block"
    },

    draggable: true,

    addAttributes() {
        return {
            src: {
                default: null,
            },
            width: {
                default: this.options.width,
            },
            height: {
                default: this.options.height,
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: "div[data-loom-video] iframe",
            },
        ]
    },

    addCommands() {
        return {
            setLoomVideo:
                (options: SetLoomVideoOptions) =>
                ({ commands }) => {
                    if (!isValidLoomUrl(options.src)) {
                        return false
                    }
                    return commands.insertContent([
                        {
                            type: "paragraph",
                            content: [],
                        },
                        {
                            type: this.name,
                            attrs: options,
                        },
                        {
                            type: "paragraph",
                            content: [],
                        },
                    ])
                },
        }
    },

    addPasteRules() {
        if (!this.options.addPasteHandler) {
            return []
        }
        return [
            nodePasteRule({
                find: LOOM_REGEX_GLOBAL,
                type: this.type,
                getAttributes: (match) => {
                    return { src: match[0] }
                },
            }),
        ]
    },

    renderHTML({ HTMLAttributes }) {
        // Use oEmbed to get the embed HTML
        // Note: This is a synchronous method, so we use the standard embed iframe
        // If you want to use the oEmbed HTML, you must fetch it before rendering
        const { src, ...rest } = HTMLAttributes
        if (!isValidLoomUrl(src)) {
            return ["span", { "data-loom-video": "", "data-invalid": "true", style: "display: none;" }]
        }
        // Extract video ID
        const match = src.match(/loom.com\/share\/([a-zA-Z0-9]+)/)
        const videoId = match ? match[1] : null
        if (!videoId) {
            return ["span", { "data-loom-video": "", "data-invalid": "true", style: "display: none;" }]
        }
        const embedUrl = `https://www.loom.com/embed/${videoId}`
        return [
            "div",
            { "data-loom-video": "" },
            [
                "iframe",
                mergeAttributes(
                    this.options.HTMLAttributes,
                    {
                        width: this.options.width,
                        height: this.options.height,
                        src: embedUrl,
                        frameborder: "0",
                        allow: "autoplay; fullscreen; picture-in-picture",
                        allowfullscreen: "true",
                    },
                    rest
                ),
            ],
        ]
    },
})
